import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MessageCircle,
  Send,
  Search,
  ArrowLeft,
  Check,
  CheckCheck,
  Plus,
  Users,
  User,
} from "lucide-react";
import { API } from "@/App";
import { format, isToday, isYesterday } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function MessagingModule({ user, businessId, onUnreadCountChange }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  
  // New conversation modal
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // User cache for displaying names
  const [userCache, setUserCache] = useState({});
  
  // Typing and presence state
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [partnerPresence, setPartnerPresence] = useState(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    fetchContacts();
    updatePresence(); // Update our own presence
    
    // Polling: Check for new messages every 10 seconds (reduced from 30)
    const pollInterval = setInterval(() => {
      fetchMessages(true); // silent fetch without loading indicator
    }, 10000);
    
    // Presence update every 60 seconds
    const presenceInterval = setInterval(() => {
      updatePresence();
    }, 60000);
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(presenceInterval);
    };
  }, [businessId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedConversation]);
  
  // Check partner's typing status when conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;
    
    const partnerId = selectedConversation.id;
    
    // Poll for typing status every 2 seconds
    const typingPollInterval = setInterval(async () => {
      const status = await checkTypingStatus(partnerId);
      setPartnerTyping(status);
    }, 2000);
    
    // Fetch partner's presence
    fetchPartnerPresence(partnerId);
    
    return () => clearInterval(typingPollInterval);
  }, [selectedConversation]);
  
  // Update own presence
  const updatePresence = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      await fetch(`${API}/messages/presence/update`, {
        method: "PUT",
        credentials: "include",
        headers,
      });
    } catch (error) {
      // Silent fail for presence updates
    }
  };
  
  // Fetch partner's last seen
  const fetchPartnerPresence = async (partnerId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/messages/presence/${partnerId}`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerPresence(data);
      }
    } catch (error) {
      console.error("Error fetching partner presence:", error);
    }
  };
  
  // Check if partner is typing
  const checkTypingStatus = async (partnerId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/messages/typing/${partnerId}`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        return data.is_typing;
      }
    } catch (error) {
      // Silent fail
    }
    return false;
  };
  
  // Send typing indicator
  const sendTypingIndicator = async (isTyping) => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      await fetch(`${API}/messages/typing/${selectedConversation.id}`, {
        method: "PUT",
        credentials: "include",
        headers,
        body: JSON.stringify({ is_typing: isTyping })
      });
    } catch (error) {
      // Silent fail
    }
  };
  
  // Handle typing detection
  const handleTyping = () => {
    sendTypingIndicator(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 3 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 3000);
  };
  
  // Format last seen time
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return t('common:unknownUser');
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now - lastSeenDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 2) return t('messaging:online');
    if (diffMins < 60) return t('common:minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common:hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('messaging:yesterday');
    return lastSeenDate.toLocaleDateString('tr-TR');
  };

  // Fetch available contacts (trainers and members for the business)
  const fetchContacts = async () => {
    if (!businessId) return;
    
    setLoadingContacts(true);
    try {
      const contacts = [];
      
      // Fetch trainers
      const trainersRes = await fetch(`${API}/businesses/${businessId}/trainers`, {
        credentials: "include",
      });
      if (trainersRes.ok) {
        const trainers = await trainersRes.json();
        trainers.forEach(tr => {
          if (tr.user_id !== user?.user_id) {
            contacts.push({
              user_id: tr.user_id,
              name: `${tr.name || ""} ${tr.surname || ""}`.trim() || t("common:trainer"),
              role: "trainer",
              avatar: null,
            });
            // Cache user info
            setUserCache(prev => ({
              ...prev,
              [tr.user_id]: {
                name: `${tr.name || ""} ${tr.surname || ""}`.trim() || t("common:trainer"),
                role: "trainer"
              }
            }));
          }
        });
      }
      
      // Fetch members
      const membersRes = await fetch(`${API}/businesses/${businessId}/members`, {
        credentials: "include",
      });
      if (membersRes.ok) {
        const members = await membersRes.json();
        members.forEach(m => {
          if (m.user_id !== user?.user_id) {
            contacts.push({
              user_id: m.user_id,
              name: `${m.name || ""} ${m.surname || ""}`.trim() || t("common:member"),
              role: "member",
              avatar: null,
            });
            // Cache user info
            setUserCache(prev => ({
              ...prev,
              [m.user_id]: {
                name: `${m.name || ""} ${m.surname || ""}`.trim() || t("common:member"),
                role: "member"
              }
            }));
          }
        });
      }
      
      setAvailableContacts(contacts);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/messages`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        
        // Group messages by conversation
        const conversationMap = new Map();
        data.forEach((msg) => {
          const otherId = msg.sender_id === user?.user_id ? msg.receiver_id : msg.sender_id;
          const otherName = msg.sender_id === user?.user_id ? msg.receiver_name : msg.sender_name;
          
          if (!conversationMap.has(otherId)) {
            conversationMap.set(otherId, {
              id: otherId,
              name: otherName || userCache[otherId]?.name || t("messaging:user"),
              messages: [],
              lastMessage: msg,
              unreadCount: 0,
            });
          }
          conversationMap.get(otherId).messages.push(msg);
          if (!msg.is_read && msg.receiver_id === user?.user_id) {
            conversationMap.get(otherId).unreadCount++;
          }
          
          // Update conversation name if we have better info
          if (otherName && otherName !== t("messaging:user")) {
            conversationMap.get(otherId).name = otherName;
          }
        });

        // Sort by last message time
        const sortedConversations = Array.from(conversationMap.values())
          .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
        
        // Check if there are new messages
        const prevCount = conversations.reduce((acc, c) => acc + c.messages.length, 0);
        const newCount = sortedConversations.reduce((acc, c) => acc + c.messages.length, 0);
        
        if (silent && newCount > prevCount) {
          toast.info(t('common:newMessageReceived'), { duration: 3000 });
        }
        
        setConversations(sortedConversations);
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: selectedConversation.id,
          content: newMessage,
        }),
      });

      if (response.ok) {
        setNewMessage("");
        fetchMessages();
      } else {
        toast.error(t('messaging:messageNotSent'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSending(false);
    }
  };

  const handleStartNewConversation = async () => {
    if (!selectedContact || !newMessage.trim()) {
      toast.error(t("common:toast_kisiSecinVeMesajYazin"));
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: selectedContact,
          content: newMessage,
        }),
      });

      if (response.ok) {
        setNewMessage("");
        setShowNewMessageModal(false);
        setSelectedContact(null);
        await fetchMessages();
        
        // Select the new conversation
        const contact = availableContacts.find(c => c.user_id === selectedContact);
        if (contact) {
          setSelectedConversation({
            id: selectedContact,
            name: contact.name,
            messages: [],
            lastMessage: null,
            unreadCount: 0,
          });
        }
        
        toast.success(t('messaging:messageSent'));
      } else {
        toast.error(t('messaging:messageNotSent'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await fetch(`${API}/messages/${messageId}/read`, {
        method: "PUT",
        credentials: "include",
      });
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  // Mark all unread messages in a conversation as read
  const markConversationAsRead = async (conv) => {
    const unreadMessages = conv.messages.filter(msg => !msg.is_read && msg.receiver_id === user?.user_id);
    
    if (unreadMessages.length > 0) {
      // Mark each message as read on the backend
      await Promise.all(unreadMessages.map(msg => handleMarkAsRead(msg.message_id)));
      
      // Update local state properly (without mutation)
      setMessages(prev => prev.map(msg => {
        if (unreadMessages.some(u => u.message_id === msg.message_id)) {
          return { ...msg, is_read: true };
        }
        return msg;
      }));
      
      // Reset unread count for this conversation
      setConversations(prev => prev.map(c => 
        c.id === conv.id ? { ...c, unreadCount: 0, messages: c.messages.map(m => 
          unreadMessages.some(u => u.message_id === m.message_id) ? { ...m, is_read: true } : m
        )} : c
      ));
      
      // Notify parent component about the change
      if (onUnreadCountChange) {
        onUnreadCountChange();
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatMessageDate = (date) => {
    const d = new Date(date);
    if (isToday(d)) return format(d, "HH:mm");
    if (isYesterday(d)) return t('messaging:yesterday') + " " + format(d, "HH:mm");
    return format(d, "dd/MM HH:mm", { locale: getDateLocale() });
  };

  const getConversationMessages = () => {
    if (!selectedConversation) return [];
    return messages
      .filter(
        (m) =>
          m.sender_id === selectedConversation.id ||
          m.receiver_id === selectedConversation.id
      )
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const filteredConversations = conversations.filter((c) => {
    if (!searchQuery) return true;
    return c.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex rounded-xl overflow-hidden border shadow-sm">
      {/* Conversations List */}
      <div
        className={`w-full md:w-80 border-r bg-white flex flex-col ${
          selectedConversation ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl text-navy">{t('common:messages')}</h2>
            <Button
              size="sm"
              className="bg-navy hover:bg-navy/90 rounded-xl"
              onClick={() => setShowNewMessageModal(true)}
              data-testid="new-message-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('common:new')}
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
            <Input
              placeholder={t("common:ph_kisiAra")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 rounded-xl"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length > 0 ? (
            filteredConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => {
                  setSelectedConversation(conv);
                  // Mark conversation as read using the new function
                  markConversationAsRead(conv);
                }}
                className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b ${
                  selectedConversation?.id === conv.id ? "bg-slate-50" : ""
                }`}
                data-testid={`conversation-${conv.id}`}
              >
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-navy text-white">
                    {getInitials(conv.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-navy truncate">{conv.name}</p>
                    <span className="text-xs text-grey flex-shrink-0 ml-2">
                      {formatMessageDate(conv.lastMessage.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-grey truncate">
                    {conv.lastMessage.sender_id === user?.user_id && "Sen: "}
                    {conv.lastMessage.content}
                  </p>
                </div>
                {conv.unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white min-w-[20px] h-5 flex items-center justify-center flex-shrink-0">
                    {conv.unreadCount}
                  </Badge>
                )}
              </button>
            ))
          ) : (
            <div className="p-8 text-center text-grey">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="mb-3">{t("common:henuzMesajinizYok")}</p>
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setShowNewMessageModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('common:startNewMessage')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div
        className={`flex-1 flex flex-col bg-slate-50 ${
          selectedConversation ? "flex" : "hidden md:flex"
        }`}
      >
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 bg-white border-b flex items-center gap-3">
              <button
                className="md:hidden p-2 hover:bg-slate-100 rounded-xl"
                onClick={() => setSelectedConversation(null)}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="relative">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-navy text-white">
                    {getInitials(selectedConversation.name)}
                  </AvatarFallback>
                </Avatar>
                {/* Online indicator */}
                {partnerPresence?.is_online && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-navy">{selectedConversation.name}</p>
                <p className="text-xs text-grey">
                  {partnerTyping ? (
                    <span className="text-navy animate-pulse">{t('messaging:typing')}</span>
                  ) : partnerPresence?.is_online ? (
                    <span className="text-green-600">{t('messaging:online')}</span>
                  ) : partnerPresence?.last_seen ? (
                    <span>{t('messaging:lastSeen')}: {formatLastSeen(partnerPresence.last_seen)}</span>
                  ) : (
                    userCache[selectedConversation.id]?.role === "trainer" ? t("common:trainer") : 
                    userCache[selectedConversation.id]?.role === "member" ? t("common:member") : ""
                  )}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {getConversationMessages().length === 0 ? (
                <div className="flex items-center justify-center h-full text-grey">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t("common:henuzMesajYok")}</p>
                    <p className="text-sm">{t('common:sendFirstMessage')}</p>
                  </div>
                </div>
              ) : (
                getConversationMessages().map((msg) => {
                  const isOwn = msg.sender_id === user?.user_id;

                  return (
                    <div
                      key={msg.message_id}
                      className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[70%] p-3 rounded-2xl ${
                          isOwn
                            ? "bg-navy text-white rounded-br-md"
                            : "bg-white text-navy rounded-bl-md shadow-sm"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div
                          className={`flex items-center justify-end gap-1 mt-1 ${
                            isOwn ? "text-white/70" : "text-grey"
                          }`}
                        >
                          <span className="text-xs">
                            {formatMessageDate(msg.created_at)}
                          </span>
                          {isOwn && (
                            msg.is_read ? (
                              <CheckCheck className="w-3 h-3" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t">
              <div className="flex gap-2">
                <Input
                  placeholder={t('messaging:typeMessage')}
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                  className="flex-1 h-12 rounded-xl"
                  data-testid="message-input"
                />
                <Button
                  className="h-12 w-12 rounded-xl bg-navy hover:bg-navy/90"
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-grey">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{t("common:birSohbetSecin")}</p>
              <p className="text-sm mb-4">{t("common:mesajlariniziGoruntulemekIcinSolTaraftan")}</p>
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                onClick={() => setShowNewMessageModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('common:startNewMessage')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('messaging:newMessage')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Contact Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t('common:to')}</label>
              {loadingContacts ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy" />
                </div>
              ) : availableContacts.length > 0 ? (
                <Select value={selectedContact || ""} onValueChange={setSelectedContact}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:ph_kisiSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableContacts.map((contact) => (
                      <SelectItem key={contact.user_id} value={contact.user_id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{contact.name}</span>
                          <Badge variant="secondary" className="text-xs ml-2">
                            {contact.role === "trainer" ? t('common:trainer') : t('common:member')}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-4 bg-slate-50 rounded-xl text-center text-grey">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t("common:mesajGonderilecekKisiBulunamadi")}</p>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t('common:messageLabel')}</label>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('messaging:typeMessage')}
                rows={3}
                className="w-full px-3 py-2 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-navy"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowNewMessageModal(false);
                  setSelectedContact(null);
                  setNewMessage("");
                }}
              >
                {t('common:giveUp')}
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleStartNewConversation}
                disabled={!selectedContact || !newMessage.trim() || sending}
              >
                {sending ? t('common:sending') : t('common:send')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
